import { Fragment } from 'react';
import { useTranslation } from 'react-i18next';
import { ContentLanguageCode } from '@/shared/constants';
import { Work } from '@/shared/types';
import { AsyncError, AsyncSkeleton } from '@/shared/components/AsyncState';
import plusIcon from '@/assets/svg/plus_icon.svg';
import { WorkCard } from './WorkCard';

type EpisodeFilter = 'all' | 'published' | 'drafts';

interface ExhibitionWorksSectionProps {
  works: Work[];
  filteredWorks: Work[];
  loading: boolean;
  error: string | null;
  currentLanguage: ContentLanguageCode;
  episodeFilter: EpisodeFilter;
  draggedWorkId: string | null;
  dragOverWorkId: string | null;
  dragOverPlace: 'before' | 'after';
  onFilterChange: (filter: EpisodeFilter) => void;
  onCreate: () => void;
  onRetry: () => void;
  onEdit: (work: Work) => void;
  onDragStart: (workId: string) => void;
  onDragOver: (e: React.DragEvent, workId: string, place: 'before' | 'after') => void;
  onDragLeave: () => void;
  onDrop: (e: React.DragEvent, targetWorkId: string, place: 'before' | 'after') => void;
  onDragEnd: () => void;
}

export const ExhibitionWorksSection: React.FC<ExhibitionWorksSectionProps> = ({
  works,
  filteredWorks,
  loading,
  error,
  currentLanguage,
  episodeFilter,
  draggedWorkId,
  dragOverWorkId,
  dragOverPlace,
  onFilterChange,
  onCreate,
  onRetry,
  onEdit,
  onDragStart,
  onDragOver,
  onDragLeave,
  onDrop,
  onDragEnd,
}) => {
  const { t } = useTranslation();

  return (
    <div className="episodes-section">
      <div className="episodes-header">
        <div className="episode-filters">
          <span>{t('exhibitions.sortBy')}:</span>
          <button
            className={`filter-btn ${episodeFilter === 'all' ? 'active' : ''}`}
            onClick={() => onFilterChange('all')}
          >
            {t('exhibitions.all')}
          </button>
          <button
            className={`filter-btn ${episodeFilter === 'published' ? 'active' : ''}`}
            onClick={() => onFilterChange('published')}
          >
            {t('exhibitions.published_plural')}
          </button>
          <button
            className={`filter-btn ${episodeFilter === 'drafts' ? 'active' : ''}`}
            onClick={() => onFilterChange('drafts')}
          >
            {t('exhibitions.drafts')}
          </button>
        </div>
      </div>

      <div className="create-episode-box" onClick={onCreate}>
        <img src={plusIcon} alt="" className="plus-icon" />
        <span>{t('exhibitions.createEpisode')}</span>
      </div>

      {loading ? (
        <AsyncSkeleton variant="cards" count={2} />
      ) : error ? (
        <AsyncError message={error} onRetry={onRetry} />
      ) : filteredWorks.length === 0 ? (
        <div className="async-state async-state--empty">
          <p className="async-state__title">
            {works.length === 0
              ? t('exhibitions.noEpisodes')
              : t('exhibitions.noEpisodesForFilter')}
          </p>
        </div>
      ) : (
        <div className="works-list">
          {filteredWorks.map((work) => (
            <Fragment key={work.id}>
              {dragOverWorkId === work.id && dragOverPlace === 'before' && (
                <div className="drag-insertion-line" aria-hidden />
              )}
              <WorkCard
                work={work}
                currentLanguage={currentLanguage}
                onEdit={onEdit}
                onDragStart={onDragStart}
                onDragOver={onDragOver}
                onDragLeave={onDragLeave}
                onDrop={onDrop}
                onDragEnd={onDragEnd}
                isDragging={draggedWorkId === work.id}
              />
              {dragOverWorkId === work.id && dragOverPlace === 'after' && (
                <div className="drag-insertion-line" aria-hidden />
              )}
            </Fragment>
          ))}
        </div>
      )}
    </div>
  );
};
